import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Settings2 } from 'lucide-react';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import WorkspaceChip from './WorkspaceChip';
import useSwitcherDnd from './useSwitcherDnd';
import './workspaces.css';

/**
 * The scope switcher (cards 1.2 / 5.2): a CompactSelectPicker over `choices`
 * in groups — Personal · My workspaces · This device · Teams — and, with
 * workspaces on, "New workspace…" / "Manage workspaces…" under a divider.
 * Workspace rows drag to reorder; a tree row dragged onto the button opens
 * the menu and drops onto Personal or a workspace (useSwitcherDnd).
 */
export default function WorkspaceSwitcher({
  choices,
  current,
  onPick,
  userName,
  workspacesOn,
  onNewWorkspace,
  onManageWorkspaces,
  onMoveTree,
  onReorder,
  ns = 'docSidebar.workspaces',
}) {
  const { t } = useTranslation();
  const dnd = useSwitcherDnd({ choices, onMoveTree, onReorder });
  const options = choices.map((c) => ({
    value: c.key,
    label: c.label,
    group: c.group,
    groupLabel: c.groupLabel,
    choice: c,
    leading: <WorkspaceChip choice={c} userName={userName} />,
  }));

  const footer = ({ close }) => (
    <>
      <button
        type="button"
        className="doc-scope-action"
        data-testid="doc-scope-new-workspace"
        onClick={() => {
          close();
          onNewWorkspace();
        }}
      >
        <Plus size={14} strokeWidth={1.8} aria-hidden />
        <span>{t(`${ns}.new`)}</span>
      </button>
      <button
        type="button"
        className="doc-scope-action"
        data-testid="doc-scope-manage-workspaces"
        onClick={() => {
          close();
          onManageWorkspaces();
        }}
      >
        <Settings2 size={14} strokeWidth={1.8} aria-hidden />
        <span>{t(`${ns}.manage`)}</span>
      </button>
    </>
  );

  return (
    <CompactSelectPicker
      value={current.key}
      onChange={onPick}
      options={options}
      className="doc-scope-picker"
      menuClassName="doc-scope-menu"
      menuMinWidth={240}
      renderFooter={workspacesOn ? footer : null}
      forceOpen={dnd.forceOpen}
      onOpenChange={dnd.onOpenChange}
      triggerProps={dnd.triggerProps}
      rowProps={dnd.rowProps}
      aria-label={t('docSidebar.scope.switch', { name: current.label })}
    />
  );
}
